/**
 * React hooks for DTN Map Tile API
 * All requests go through the server-side proxy for security
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import {
  getProxiedDTNCatalog,
  getProxiedDTNLayerMetadata,
  getProxiedDTNLayerTimestamps,
  getProxiedDTNLayerStyles,
  getProxiedDTNLayerLegend,
  buildProxiedDTNTileUrlTemplate,
  checkDTNProxyStatus,
} from './dtnProxyClient';
import {
  DTNTimestampWrapper,
  DTNLayerConfig,
} from './types';

// Query keys
const DTN_QUERY_KEYS = {
  proxyStatus: ['dtn', 'proxy-status'] as const,
  catalog: (locale?: string) => ['dtn', 'catalog', locale] as const,
  metadata: (layerId: string, locale?: string) => ['dtn', 'metadata', layerId, locale] as const,
  timestamps: (layerId: string) => ['dtn', 'timestamps', layerId] as const,
  styles: (layerId: string, locale?: string) => ['dtn', 'styles', layerId, locale] as const,
  legend: (layerId: string, unit?: string, locale?: string) => ['dtn', 'legend', layerId, unit, locale] as const,
};

/**
 * Hook to check if DTN proxy is available
 */
export function useDTNProxyStatus() {
  return useQuery({
    queryKey: DTN_QUERY_KEYS.proxyStatus,
    queryFn: checkDTNProxyStatus,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

/**
 * Hook to fetch DTN catalog
 * @param token - Ignored, kept for backwards compatibility
 */
export function useDTNCatalog(token?: string | undefined, locale?: string) {
  return useQuery({
    queryKey: DTN_QUERY_KEYS.catalog(locale),
    queryFn: () => getProxiedDTNCatalog(locale),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch layer metadata
 * @param token - Ignored, kept for backwards compatibility
 */
export function useDTNLayerMetadata(
  token: string | undefined,
  layerId: string | undefined,
  locale?: string
) {
  return useQuery({
    queryKey: DTN_QUERY_KEYS.metadata(layerId || '', locale),
    queryFn: () => {
      if (!layerId) throw new Error('layerId not available');
      return getProxiedDTNLayerMetadata(layerId, locale);
    },
    enabled: !!layerId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch layer timestamps
 * @param token - Ignored, kept for backwards compatibility
 */
export function useDTNLayerTimestamps(
  token: string | undefined,
  layerId: string | undefined,
  options?: { refetchInterval?: number; forceOffInterval?: boolean }
) {
  return useQuery({
    queryKey: DTN_QUERY_KEYS.timestamps(layerId || ''),
    queryFn: () => {
      if (!layerId) throw new Error('layerId not available');
      return getProxiedDTNLayerTimestamps(layerId, undefined, options?.forceOffInterval);
    },
    enabled: !!layerId,
    staleTime: 30 * 1000,
    refetchInterval: options?.refetchInterval || 60 * 1000,
  });
}

/**
 * Hook to fetch layer styles
 * @param token - Ignored, kept for backwards compatibility
 */
export function useDTNLayerStyles(
  token: string | undefined,
  layerId: string | undefined,
  locale?: string
) {
  return useQuery({
    queryKey: DTN_QUERY_KEYS.styles(layerId || '', locale),
    queryFn: () => {
      if (!layerId) throw new Error('layerId not available');
      return getProxiedDTNLayerStyles(layerId, locale);
    },
    enabled: !!layerId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch layer legend
 * @param token - Ignored, kept for backwards compatibility
 */
export function useDTNLayerLegend(
  token: string | undefined,
  layerId: string | undefined,
  unit?: string,
  locale?: string
) {
  return useQuery({
    queryKey: DTN_QUERY_KEYS.legend(layerId || '', unit, locale),
    queryFn: () => {
      if (!layerId) throw new Error('layerId not available');
      return getProxiedDTNLayerLegend(layerId, unit, locale);
    },
    enabled: !!layerId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to get the latest tile set ID for a layer
 * @param token - Ignored, kept for backwards compatibility
 */
export function useLatestDTNTileSetId(
  token: string | undefined,
  layerId: string | undefined
) {
  const { data: timestamps, ...rest } = useDTNLayerTimestamps(token, layerId);

  const latestTileSetId = useMemo(() => {
    if (!timestamps || timestamps.length === 0) return null;
    const latestWrapper = timestamps[timestamps.length - 1];
    if (!latestWrapper?.timestamps || latestWrapper.timestamps.length === 0) return null;
    const latestTimestamp = latestWrapper.timestamps[latestWrapper.timestamps.length - 1];
    return latestTimestamp?.tileSetId || null;
  }, [timestamps]);

  return { latestTileSetId, timestamps, ...rest };
}

/**
 * Hook to build a tile URL template for a DTN layer
 * @param token - Ignored, kept for backwards compatibility
 */
export function useDTNTileUrlTemplate(
  token: string | undefined,
  layerId: string | undefined,
  tileSetId: string | null | undefined,
  size: 256 | 512 = 256
) {
  return useMemo(() => {
    if (!layerId || !tileSetId) return null;
    return buildProxiedDTNTileUrlTemplate(layerId, tileSetId, size);
  }, [layerId, tileSetId, size]);
}

/**
 * Legacy hook for backwards compatibility - token is handled by proxy
 * @deprecated Token is now handled server-side via proxy
 */
export function useDTNToken(_config: unknown) {
  return { data: 'proxy', isLoading: false, error: null };
}

/**
 * Combined hook for easy DTN layer setup
 */
export function useDTNWeatherLayer(
  config: unknown,
  layerConfig: DTNLayerConfig | null
) {
  const { data: token } = useDTNToken(config);
  
  const { 
    latestTileSetId, 
    timestamps,
    isLoading: isTimestampsLoading 
  } = useLatestDTNTileSetId(token, layerConfig?.layerId);

  const { 
    data: metadata, 
    isLoading: isMetadataLoading 
  } = useDTNLayerMetadata(token, layerConfig?.layerId, layerConfig?.locale);

  const { 
    data: legend, 
  } = useDTNLayerLegend(
    token, 
    layerConfig?.layerId, 
    layerConfig?.unit, 
    layerConfig?.locale
  );

  const tileUrlTemplate = useDTNTileUrlTemplate(
    token,
    layerConfig?.layerId,
    latestTileSetId,
    layerConfig?.tileSize
  );

  const isLoading = isTimestampsLoading || isMetadataLoading;
  const isReady = !!tileUrlTemplate && !!metadata;

  return {
    token,
    tileUrlTemplate,
    tileSetId: latestTileSetId,
    timestamps,
    metadata,
    legend,
    isLoading,
    isReady,
    error: null,
  };
}

/**
 * Hook to manage DTN layer state (selected timestamp, etc.)
 */
export function useDTNLayerState(timestamps: DTNTimestampWrapper[] | undefined) {
  const [selectedTimestampIndex, setSelectedTimestampIndex] = useState<number>(-1);

  const allTimestamps = useMemo(() => {
    if (!timestamps || timestamps.length === 0) return [];
    return timestamps.flatMap(wrapper => wrapper.timestamps || []);
  }, [timestamps]);

  const selectedTileSetId = useMemo(() => {
    if (selectedTimestampIndex < 0 || selectedTimestampIndex >= allTimestamps.length) {
      return allTimestamps[allTimestamps.length - 1]?.tileSetId || null;
    }
    return allTimestamps[selectedTimestampIndex]?.tileSetId || null;
  }, [allTimestamps, selectedTimestampIndex]);

  const selectedValidTime = useMemo(() => {
    if (selectedTimestampIndex < 0 || selectedTimestampIndex >= allTimestamps.length) {
      return allTimestamps[allTimestamps.length - 1]?.valid || null;
    }
    return allTimestamps[selectedTimestampIndex]?.valid || null;
  }, [allTimestamps, selectedTimestampIndex]);

  const goToLatest = useCallback(() => {
    setSelectedTimestampIndex(-1);
  }, []);

  const goToTimestamp = useCallback((index: number) => {
    if (index >= 0 && index < allTimestamps.length) {
      setSelectedTimestampIndex(index);
    }
  }, [allTimestamps.length]);

  const goToPrevious = useCallback(() => {
    const currentIndex = selectedTimestampIndex < 0 ? allTimestamps.length - 1 : selectedTimestampIndex;
    if (currentIndex > 0) {
      setSelectedTimestampIndex(currentIndex - 1);
    }
  }, [selectedTimestampIndex, allTimestamps.length]);

  const goToNext = useCallback(() => {
    const currentIndex = selectedTimestampIndex < 0 ? allTimestamps.length - 1 : selectedTimestampIndex;
    if (currentIndex < allTimestamps.length - 1) {
      setSelectedTimestampIndex(currentIndex + 1);
    } else {
      setSelectedTimestampIndex(-1);
    }
  }, [selectedTimestampIndex, allTimestamps.length]);

  return {
    allTimestamps,
    selectedTileSetId,
    selectedValidTime,
    selectedTimestampIndex: selectedTimestampIndex < 0 ? allTimestamps.length - 1 : selectedTimestampIndex,
    isLatest: selectedTimestampIndex < 0,
    goToLatest,
    goToTimestamp,
    goToPrevious,
    goToNext,
    canGoPrevious: (selectedTimestampIndex < 0 ? allTimestamps.length - 1 : selectedTimestampIndex) > 0,
    canGoNext: selectedTimestampIndex >= 0 && selectedTimestampIndex < allTimestamps.length - 1,
  };
}

export { DTN_QUERY_KEYS };
